import { RobotOutlined, StarFilled } from '@ant-design/icons'
import {
	Button,
	Input,
	Modal,
	Popconfirm,
	Space,
	Table,
	Tag,
	Typography,
	message,
} from 'antd'
import { useEffect, useMemo, useState } from 'react'
import api from '../../api/index.api'
import { AIPromptPreviewModal, ListPageIntro, ListPageShell } from '../../common'
import { usePromptPreview } from '../../hooks'

const { Paragraph, Title } = Typography
const INITIAL_REFINE_STATE = {
	open: false,
	feedback: '',
	record: null,
	loading: false,
}
const INITIAL_EDIT_STATE = {
	open: false,
	record: null,
	idea: '',
	objective: '',
	expectedImpact: '',
	features: '',
	userFlow: '',
	loading: false,
}
const AUTO_GENERATE_INTERVAL_SEC = 60
const AUTO_GENERATE_MAX_RUNS = 5

const useAutoGenerateTimer = runGenerateDraftWithAI => {
	const [isAutoTimerRunning, setIsAutoTimerRunning] = useState(false)
	const [autoCountdownSec, setAutoCountdownSec] = useState(
		AUTO_GENERATE_INTERVAL_SEC
	)
	const [autoRunCount, setAutoRunCount] = useState(0)

	const stopAutoTimer = () => {
		setIsAutoTimerRunning(false)
		setAutoCountdownSec(AUTO_GENERATE_INTERVAL_SEC)
		setAutoRunCount(0)
	}

	const startAutoTimer = () => {
		setIsAutoTimerRunning(true)
		setAutoCountdownSec(AUTO_GENERATE_INTERVAL_SEC)
		setAutoRunCount(0)
	}

	useEffect(() => {
		if (!isAutoTimerRunning) return
		if (autoRunCount >= AUTO_GENERATE_MAX_RUNS) {
			setIsAutoTimerRunning(false)
			return
		}

		const timer = setTimeout(async () => {
			if (autoCountdownSec > 1) {
				setAutoCountdownSec(prev => prev - 1)
				return
			}

			const ok = await runGenerateDraftWithAI(false)
			const nextRunCount = autoRunCount + 1
			setAutoRunCount(nextRunCount)
			if (ok) {
				message.success(
					`Auto Generate with AI executed (${nextRunCount}/${AUTO_GENERATE_MAX_RUNS}).`
				)
			}

			if (nextRunCount >= AUTO_GENERATE_MAX_RUNS) {
				setIsAutoTimerRunning(false)
				setAutoCountdownSec(AUTO_GENERATE_INTERVAL_SEC)
				return
			}
			setAutoCountdownSec(AUTO_GENERATE_INTERVAL_SEC)
		}, 1000)

		return () => clearTimeout(timer)
	}, [runGenerateDraftWithAI, isAutoTimerRunning, autoCountdownSec, autoRunCount])

	return {
		isAutoTimerRunning,
		autoCountdownSec,
		autoRunCount,
		startAutoTimer,
		stopAutoTimer,
	}
}

export const AgentsProductOwner = () => {
	const [ideas, setIdeas] = useState([])
	const [activeAgentConfig, setActiveAgentConfig] = useState(null)
	const [draftIdea, setDraftIdea] = useState({
		idea: '',
		objective: '',
		expectedImpact: '',
	})
	const [isGeneratingDraft, setIsGeneratingDraft] = useState(false)
	const [isPreviewingPrompt, setIsPreviewingPrompt] = useState(false)
	const [isPreviewingRefinePrompt, setIsPreviewingRefinePrompt] =
		useState(false)
	const [isPreviewingExplainPrompt, setIsPreviewingExplainPrompt] =
		useState(false)
	const { preview: promptPreview, openPreview, resetPreview } =
		usePromptPreview('Full prompt preview')
	const [isLoadingIdeas, setIsLoadingIdeas] = useState(false)
	const [aiStats, setAiStats] = useState(null)
	const [refineState, setRefineState] = useState(INITIAL_REFINE_STATE)
	const [editState, setEditState] = useState(INITIAL_EDIT_STATE)
	const [detailRecord, setDetailRecord] = useState(null)
	const [ideaDeepDive, setIdeaDeepDive] = useState('')
	const [isLoadingDeepDive, setIsLoadingDeepDive] = useState(false)

	const loadIdeas = async () => {
		setIsLoadingIdeas(true)
		try {
			const rs = await api.agentsAdminApi.getProductOwnerIdeaDrafts()
			const normalizedIdeas = Array.isArray(rs)
				? rs
				: Array.isArray(rs?.data)
					? rs.data
					: []
			setIdeas(normalizedIdeas)
		} catch (err) {
			message.error(
				err?.message ||
					'Could not load product owner ideas from backend.'
			)
		} finally {
			setIsLoadingIdeas(false)
		}
	}

	const loadAIStats = async () => {
		try {
			const rs = await api.agentsAdminApi.getProductOwnerAIStats()
			setAiStats(rs || null)
		} catch {
			setAiStats(null)
		}
	}

	const loadActiveAgentConfig = async () => {
		try {
			const rs = await api.agentsAdminApi.getActiveAgentConfig('product_owner')
			setActiveAgentConfig(rs || null)
		} catch {
			setActiveAgentConfig(null)
		}
	}

	useEffect(() => {
		loadIdeas()
		loadAIStats()
		loadActiveAgentConfig()
	}, [])

	const handleToggleApproval = async record => {
		try {
			const updated = await api.agentsAdminApi.updateProductOwnerIdeaApproval(
				record.id,
				!record.approved
			)
			setIdeas(prevIdeas =>
				prevIdeas.map(idea =>
					idea.id === updated.id ? updated : idea
				)
			)
			loadAIStats()
		} catch (err) {
			message.error(
				err?.message || 'Could not update approval status.'
			)
		}
	}

	const handleAddDraftIdea = async () => {
		const idea = draftIdea.idea.trim()
		const objective = draftIdea.objective.trim()
		const expectedImpact = draftIdea.expectedImpact.trim()

		if (!idea || !objective || !expectedImpact) return

		try {
			const created = await api.agentsAdminApi.createProductOwnerIdeaDraft({
				idea,
				objective,
				expectedImpact,
				owner: 'Product Owner',
			})
			setIdeas(prevIdeas => [created, ...prevIdeas])
			setDraftIdea({ idea: '', objective: '', expectedImpact: '' })
			loadAIStats()
		} catch (err) {
			message.error(err?.message || 'Could not create draft idea.')
		}
	}

	const runGenerateDraftWithAI = async (showSuccessMessage = true) => {
		const prompt = [
			draftIdea.idea && `Idea seed: ${draftIdea.idea}`,
			draftIdea.objective && `Objective seed: ${draftIdea.objective}`,
			draftIdea.expectedImpact &&
				`Expected impact seed: ${draftIdea.expectedImpact}`,
		]
			.filter(Boolean)
			.join('. ')

		setIsGeneratingDraft(true)
		try {
			const generated =
				await api.agentsAdminApi.generateProductOwnerIdeaDraft(prompt)
			setIdeas(prevIdeas => [generated, ...prevIdeas])
			setDraftIdea({ idea: '', objective: '', expectedImpact: '' })
			if (showSuccessMessage) {
				message.success('Generated and saved draft idea with AI.')
			}
			loadAIStats()
			return true
		} catch (err) {
			message.error(
				err?.message ||
					'Could not generate AI draft. Check backend Vertex config.'
			)
			return false
		} finally {
			setIsGeneratingDraft(false)
		}
	}

	const handleGenerateDraftWithAI = async () => {
		await runGenerateDraftWithAI(true)
	}

	const handlePreviewGeneratePrompt = async () => {
		const prompt = [
			draftIdea.idea && `Idea seed: ${draftIdea.idea}`,
			draftIdea.objective && `Objective seed: ${draftIdea.objective}`,
			draftIdea.expectedImpact &&
				`Expected impact seed: ${draftIdea.expectedImpact}`,
		]
			.filter(Boolean)
			.join('. ')

		setIsPreviewingPrompt(true)
		try {
			const rs = await api.agentsAdminApi.previewProductOwnerGeneratePrompt(
				prompt
			)
			openPreview({
				title: 'Full prompt preview (Generate)',
				prompt: rs?.prompt || '',
				model: rs?.model || '(default)',
				temperature: Number(rs?.temperature || 0),
				maxOutputTokens: Number(rs?.maxOutputTokens || 0),
			})
		} catch (err) {
			message.error(err?.message || 'Could not preview full AI prompt.')
		} finally {
			setIsPreviewingPrompt(false)
		}
	}

	const handlePreviewRefinePrompt = async () => {
		const feedback = refineState.feedback.trim()
		const recordId = refineState?.record?.id
		if (!recordId) return
		if (!feedback) {
			message.warning('Please enter feedback to preview refine prompt.')
			return
		}

		setIsPreviewingRefinePrompt(true)
		try {
			const rs =
				await api.agentsAdminApi.previewRefineProductOwnerIdeaDraftPrompt(
					recordId,
					feedback
				)
			openPreview({
				title: 'Full prompt preview (Refine)',
				prompt: rs?.prompt || '',
				model: rs?.model || '(default)',
				temperature: Number(rs?.temperature || 0),
				maxOutputTokens: Number(rs?.maxOutputTokens || 0),
			})
		} catch (err) {
			message.error(err?.message || 'Could not preview refine prompt.')
		} finally {
			setIsPreviewingRefinePrompt(false)
		}
	}

	const handlePreviewExplainPrompt = async () => {
		const recordId = detailRecord?.id
		if (!recordId) return
		setIsPreviewingExplainPrompt(true)
		try {
			const rs =
				await api.agentsAdminApi.previewExplainProductOwnerIdeaDraftPrompt(
					recordId
				)
			openPreview({
				title: 'Full prompt preview (Explain)',
				prompt: rs?.prompt || '',
				model: rs?.model || '(default)',
				temperature: Number(rs?.temperature || 0),
				maxOutputTokens: Number(rs?.maxOutputTokens || 0),
			})
		} catch (err) {
			message.error(err?.message || 'Could not preview explain prompt.')
		} finally {
			setIsPreviewingExplainPrompt(false)
		}
	}

	const {
		isAutoTimerRunning,
		autoCountdownSec,
		autoRunCount,
		startAutoTimer,
		stopAutoTimer,
	} = useAutoGenerateTimer(runGenerateDraftWithAI)

	const openRefineModal = record => {
		setRefineState({
			open: true,
			feedback: '',
			record,
			loading: false,
		})
	}

	const handleConfirmRefine = async () => {
		const feedback = refineState.feedback.trim()
		if (!feedback) {
			message.warning('Please enter feedback for AI refinement.')
			return
		}
		setRefineState(prev => ({ ...prev, loading: true }))
		try {
			const updated = await api.agentsAdminApi.refineProductOwnerIdeaDraft(
				refineState.record.id,
				feedback
			)
			setIdeas(prevIdeas =>
				prevIdeas.map(idea =>
					idea.id === updated.id ? updated : idea
				)
			)
			loadAIStats()
			message.success('Idea updated from your feedback.')
			setRefineState(INITIAL_REFINE_STATE)
		} catch (err) {
			message.error(err?.message || 'Could not refine idea with AI.')
			setRefineState(prev => ({ ...prev, loading: false }))
		}
	}

	const handleDeleteIdea = async record => {
		try {
			await api.agentsAdminApi.deleteProductOwnerIdeaDraft(record.id)
			setIdeas(prevIdeas =>
				prevIdeas.filter(item => item.id !== record.id)
			)
			if (detailRecord?.id === record.id) {
				setDetailRecord(null)
				setIdeaDeepDive('')
				setIsLoadingDeepDive(false)
			}
			if (refineState?.record?.id === record.id) {
				setRefineState(INITIAL_REFINE_STATE)
			}
			loadAIStats()
			message.success('Idea draft deleted.')
		} catch (err) {
			message.error(err?.message || 'Could not delete idea draft.')
		}
	}

	const openEditModal = record => {
		setEditState({
			open: true,
			record,
			idea: record.idea || '',
			objective: record.objective || '',
			expectedImpact: record.expectedImpact || '',
			features: record.features || '',
			userFlow: record.userFlow || '',
			loading: false,
		})
	}

	const handleConfirmEdit = async () => {
		const idea = editState.idea.trim()
		const objective = editState.objective.trim()
		const expectedImpact = editState.expectedImpact.trim()
		const features = editState.features.trim()
		const userFlow = editState.userFlow.trim()
		if (!idea || !objective || !expectedImpact || !features || !userFlow) {
			message.warning(
				'Please fill Idea, Objective, Expected Impact, Features, and User flow.'
			)
			return
		}

		setEditState(prev => ({ ...prev, loading: true }))
		try {
			const updated = await api.agentsAdminApi.updateProductOwnerIdeaDraft(
				editState.record.id,
				{ idea, objective, expectedImpact, features, userFlow }
			)
			setIdeas(prevIdeas =>
				prevIdeas.map(item => (item.id === updated.id ? updated : item))
			)
			if (detailRecord?.id === updated.id) {
				setDetailRecord(updated)
				setIdeaDeepDive(updated?.detailedBrief || '')
			}
			setEditState(INITIAL_EDIT_STATE)
			message.success('Idea draft updated.')
		} catch (err) {
			message.error(err?.message || 'Could not update idea draft.')
			setEditState(prev => ({ ...prev, loading: false }))
		}
	}

	const columns = useMemo(
		() => [
			{
				title: 'Idea',
				dataIndex: 'idea',
				key: 'idea',
			},
			{
				title: 'Objective',
				dataIndex: 'objective',
				key: 'objective',
			},
			{
				title: 'Expected Impact',
				dataIndex: 'expectedImpact',
				key: 'expectedImpact',
			},
			{
				title: 'Features',
				dataIndex: 'features',
				key: 'features',
				width: 420,
				render: value => (
					<span style={{ whiteSpace: 'pre-wrap' }}>{value || ''}</span>
				),
			},
			{
				title: 'User flow',
				dataIndex: 'userFlow',
				key: 'userFlow',
				render: value => (
					<span style={{ whiteSpace: 'pre-wrap' }}>{value || ''}</span>
				),
			},
			{
				title: 'Status',
				dataIndex: 'approved',
				key: 'status',
				render: approved =>
					approved ? (
						<Tag color="green">Approved</Tag>
					) : (
						<Tag color="gold">Pending</Tag>
					),
			},
			{
				title: 'Action',
				key: 'action',
				render: (_, record) => (
					<Space size={8} wrap style={{ maxWidth: 260 }}>
						<Button
							size="small"
							onClick={() => {
								setDetailRecord(record)
								setIdeaDeepDive(record?.detailedBrief || '')
							}}
						>
							View details
						</Button>
						<Button size="small" onClick={() => openEditModal(record)}>
							Edit
						</Button>
						<Button
							type={record.approved ? 'default' : 'primary'}
							size="small"
							onClick={() => handleToggleApproval(record)}
						>
							{record.approved ? 'Disapprove' : 'Approve'}
						</Button>
						<Button
							size="small"
							icon={<StarFilled style={{ color: '#faad14' }} />}
							onClick={() => openRefineModal(record)}
						>
							Refine
						</Button>
						<Popconfirm
							title="Delete this idea draft?"
							description="This action cannot be undone."
							okText="Delete"
							cancelText="Cancel"
							okButtonProps={{ danger: true }}
							onConfirm={() => handleDeleteIdea(record)}
						>
							<Button size="small" danger>
								Delete
							</Button>
						</Popconfirm>
					</Space>
				),
			},
		],
		[
			detailRecord?.id,
			refineState?.record?.id,
			isLoadingDeepDive,
			aiStats,
		]
	)

	const approvedIdeas = ideas.filter(idea => idea.approved)

	return (
		<ListPageShell>
			<ListPageIntro
				icon={RobotOutlined}
				title="Agent: Product Owner"
				subtitle="Manage Product Owner agent settings and prompts."
			/>
			<Paragraph>
				Objective: deliver ideas that help improve WordsMine revenue,
				user retention, reduce churn, and increase product stickiness.
			</Paragraph>
			<Paragraph type="secondary" style={{ marginBottom: 8 }}>
				Knowledge document path:{' '}
				<code>{activeAgentConfig?.knowledgeDocPath || '(not configured)'}</code>
			</Paragraph>
			{aiStats && (
				<div style={{ marginBottom: 12 }}>
					<Space size={12} style={{ marginBottom: 8 }} wrap>
						<Tag color="blue">AI calls: {aiStats.totalCalls || 0}</Tag>
						<Tag color="cyan">
							Input tokens: {aiStats.totalInputTokens || 0}
						</Tag>
						<Tag color="magenta">
							Output tokens: {aiStats.totalOutputTokens || 0}
						</Tag>
						<Tag color="purple">
							Total tokens: {aiStats.totalTokens || 0}
						</Tag>
						<Tag color="geekblue">
							Est. cost (USD): {(aiStats.totalCostUsd || 0).toFixed(6)}
						</Tag>
					</Space>
					<Paragraph type="secondary" style={{ marginBottom: 0 }}>
						Formula: (input_tokens / 1,000,000 *{' '}
						{Number(
							aiStats.appliedInputPriceUsdPer1M || 0
						).toFixed(2)}
						) + (output_tokens / 1,000,000 *{' '}
						{Number(
							aiStats.appliedOutputPriceUsdPer1M || 0
						).toFixed(2)}
						). Prices are read from backend config
						<code>ai.vertex.inputPriceUsdPer1M</code> and
						<code>ai.vertex.outputPriceUsdPer1M</code>, and cost is
						recalculated from token usage on every stats load. Estimates
						exclude network/infra costs.
					</Paragraph>
				</div>
			)}
			<Space direction="vertical" size={16} style={{ width: '100%' }}>
				<div>
					<Title level={5}>All idea drafts (PRD-style)</Title>
					<Space
						size={8}
						style={{ width: '100%', marginBottom: 12 }}
						wrap
					>
						<Input
							placeholder="Idea"
							value={draftIdea.idea}
							onChange={e =>
								setDraftIdea(prev => ({
									...prev,
									idea: e.target.value,
								}))
							}
							style={{ minWidth: 260 }}
						/>
						<Input
							placeholder="Objective"
							value={draftIdea.objective}
							onChange={e =>
								setDraftIdea(prev => ({
									...prev,
									objective: e.target.value,
								}))
							}
							style={{ minWidth: 220 }}
						/>
						<Input
							placeholder="Expected impact"
							value={draftIdea.expectedImpact}
							onChange={e =>
								setDraftIdea(prev => ({
									...prev,
									expectedImpact: e.target.value,
								}))
							}
							onPressEnter={handleAddDraftIdea}
							style={{ minWidth: 260 }}
						/>
						<Button type="primary" onClick={handleAddDraftIdea}>
							Add draft idea
						</Button>
						<Button
							onClick={handleGenerateDraftWithAI}
							loading={isGeneratingDraft}
							icon={<StarFilled style={{ color: '#faad14' }} />}
						>
							Generate
						</Button>
						<Button
							onClick={handlePreviewGeneratePrompt}
							loading={isPreviewingPrompt}
						>
							Preview prompt
						</Button>
						<Button
							onClick={startAutoTimer}
							disabled={isAutoTimerRunning}
						>
							Start timer
						</Button>
						<Button
							onClick={stopAutoTimer}
							disabled={!isAutoTimerRunning && autoRunCount === 0}
						>
							Stop / Reset timer
						</Button>
					</Space>
					<Paragraph type="secondary" style={{ marginBottom: 8 }}>
						Auto generate timer: {isAutoTimerRunning ? 'Running' : 'Idle'}
						{' | '}next call in: {autoCountdownSec}s{' | '}executed:{' '}
						{autoRunCount}/{AUTO_GENERATE_MAX_RUNS}
					</Paragraph>
					<Table
						rowKey="id"
						columns={columns}
						dataSource={ideas}
						loading={isLoadingIdeas}
						scroll={{ x: 1500 }}
						pagination={false}
					/>
				</div>
				<div>
					<Title level={5}>Approved ideas</Title>
					<Table
						rowKey="id"
						columns={columns}
						dataSource={approvedIdeas}
						loading={isLoadingIdeas}
						scroll={{ x: 1500 }}
						pagination={false}
					/>
				</div>
			</Space>
			<AIPromptPreviewModal
				visible={promptPreview.visible}
				title={promptPreview.title}
				width={900}
				onCancel={resetPreview}
				prompt={promptPreview.prompt}
				model={promptPreview.model}
				temperature={promptPreview.temperature}
				maxOutputTokens={promptPreview.maxOutputTokens}
				maskZeroMaxOutputTokens
				preStyle={{ maxHeight: 560 }}
			/>
			<Modal
				title="Refine draft with feedback (Idea, Objective, Expected Impact)"
				visible={refineState.open}
				onCancel={() => setRefineState(INITIAL_REFINE_STATE)}
				onOk={handleConfirmRefine}
				okText="Update"
				okButtonProps={{
					icon: <StarFilled style={{ color: '#faad14' }} />,
				}}
				confirmLoading={refineState.loading}
			>
				<Space direction="vertical" style={{ width: '100%' }}>
					{refineState.record && (
						<Paragraph type="secondary">
							Updating Idea, Objective, Expected Impact, Features, and User flow for this draft.
							<br />
							Current idea: {refineState.record.idea}
						</Paragraph>
					)}
					<Input.TextArea
						rows={4}
						placeholder="Describe how AI should update Idea, Objective, Expected Impact, Features, and User flow. Example: make it shorter, target TOEIC users in Vietnam, include measurable KPI."
						value={refineState.feedback}
						onChange={e =>
							setRefineState(prev => ({
								...prev,
								feedback: e.target.value,
							}))
						}
					/>
					<Button
						onClick={handlePreviewRefinePrompt}
						loading={isPreviewingRefinePrompt}
					>
						Preview refine prompt
					</Button>
				</Space>
			</Modal>
			<Modal
				title="Edit idea draft"
				visible={editState.open}
				onCancel={() => setEditState(INITIAL_EDIT_STATE)}
				onOk={handleConfirmEdit}
				okText="Save changes"
				confirmLoading={editState.loading}
			>
				<Space direction="vertical" style={{ width: '100%' }}>
					<Input
						placeholder="Idea"
						value={editState.idea}
						onChange={e =>
							setEditState(prev => ({
								...prev,
								idea: e.target.value,
							}))
						}
					/>
					<Input
						placeholder="Objective"
						value={editState.objective}
						onChange={e =>
							setEditState(prev => ({
								...prev,
								objective: e.target.value,
							}))
						}
					/>
					<Input.TextArea
						rows={4}
						placeholder="Expected Impact"
						value={editState.expectedImpact}
						onChange={e =>
							setEditState(prev => ({
								...prev,
								expectedImpact: e.target.value,
							}))
						}
					/>
					<Input.TextArea
						rows={5}
						placeholder="Features (numbered list, user story format)"
						value={editState.features}
						onChange={e =>
							setEditState(prev => ({
								...prev,
								features: e.target.value,
							}))
						}
					/>
					<Input.TextArea
						rows={5}
						placeholder="User flow"
						value={editState.userFlow}
						onChange={e =>
							setEditState(prev => ({
								...prev,
								userFlow: e.target.value,
							}))
						}
					/>
				</Space>
			</Modal>
			<Modal
				title="Idea draft details"
				visible={!!detailRecord}
				footer={null}
				onCancel={() => {
					setDetailRecord(null)
					setIdeaDeepDive('')
					setIsLoadingDeepDive(false)
				}}
				width={900}
			>
				{detailRecord && (
					<Space direction="vertical" size={12} style={{ width: '100%' }}>
						<Paragraph>
							<strong>Status:</strong>{' '}
							{detailRecord.approved ? 'Approved' : 'Pending'}
						</Paragraph>
						<Paragraph>
							<strong>Owner:</strong>{' '}
							{detailRecord.owner || 'Product Owner'}
						</Paragraph>
						<Paragraph>
							<strong>Idea</strong>
							<br />
							{detailRecord.idea}
						</Paragraph>
						<Paragraph>
							<strong>Objective</strong>
							<br />
							{detailRecord.objective}
						</Paragraph>
						<Paragraph>
							<strong>Expected Impact</strong>
							<br />
							{detailRecord.expectedImpact}
						</Paragraph>
						<Paragraph>
							<strong>Features</strong>
							<br />
							<span style={{ whiteSpace: 'pre-wrap' }}>
								{detailRecord.features || ''}
							</span>
						</Paragraph>
						<Paragraph>
							<strong>User flow</strong>
							<br />
							<span style={{ whiteSpace: 'pre-wrap' }}>
								{detailRecord.userFlow || ''}
							</span>
						</Paragraph>
						{detailRecord.sourcePrompt && (
							<Paragraph>
								<strong>Source / Feedback Prompt</strong>
								<br />
								{detailRecord.sourcePrompt}
							</Paragraph>
						)}
						<Paragraph type="secondary">
							Generated by AI: {detailRecord.generatedByAI ? 'Yes' : 'No'}
						</Paragraph>
						<Space size={8}>
							<Button
								loading={isPreviewingExplainPrompt}
								onClick={handlePreviewExplainPrompt}
							>
								Preview explain prompt
							</Button>
							<Button
								loading={isLoadingDeepDive}
								icon={<StarFilled style={{ color: '#faad14' }} />}
								onClick={async () => {
									setIsLoadingDeepDive(true)
									try {
										const rs =
											await api.agentsAdminApi.explainProductOwnerIdeaDraft(
												detailRecord.id
											)
										const updatedRecord =
											rs?.record || {
												...detailRecord,
												detailedBrief: rs?.details || '',
											}
										setIdeaDeepDive(
											updatedRecord.detailedBrief || ''
										)
										setDetailRecord(updatedRecord)
										setIdeas(prevIdeas =>
											prevIdeas.map(idea =>
												idea.id === updatedRecord.id
													? updatedRecord
													: idea
											)
										)
									} catch (err) {
										message.error(
											err?.message ||
												'Could not load detailed explanation.'
										)
									} finally {
										setIsLoadingDeepDive(false)
									}
								}}
							>
								Explain with AI
							</Button>
						</Space>
						{ideaDeepDive && (
							<Paragraph>
								<strong>AI detailed brief</strong>
								<br />
								<span style={{ whiteSpace: 'pre-wrap' }}>
									{ideaDeepDive}
								</span>
							</Paragraph>
						)}
						{aiStats?.recentCalls?.length > 0 && (
							<div>
								<strong>Recent AI calls for this idea</strong>
								{aiStats.recentCalls
									.filter(
										item =>
											item.ideaDraftId === detailRecord.id
									)
									.slice(0, 5)
									.map(item => (
										<Paragraph
											key={item.id}
											type="secondary"
											style={{ marginBottom: 4 }}
										>
											{item.callType} | {item.model} | in:
											{item.inputTokens} out:
											{item.outputTokens} total:
											{item.totalTokens} | $
											{Number(
												item.estimatedCostUsd || 0
											).toFixed(6)}
										</Paragraph>
									))}
							</div>
						)}
					</Space>
				)}
			</Modal>
		</ListPageShell>
	)
}
